import { DialogCloseEvent } from '@fc/dialog';
import { FCEvents, MediaPlayEvent } from '@fc/events';

/** A mixed-media sequence owned by one reusable Stories block. */
class StoryCircles extends HTMLElement {
  #lifetime;
  #slideLifetime;
  #items = [];
  #overlay;
  #dialog;
  #index = 0;
  #frame = 0;
  #lastTime = 0;
  #elapsed = 0;
  #paused = true;
  #muted = true;
  #ready = false;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    if (this.#lifetime) return;
    this.#lifetime = new AbortController();
    this.#items = [...this.querySelectorAll('[data-circle-item]')].filter(item => item.querySelector('[data-circle-media]'));
    this.#items.forEach(item => { item.querySelector('button').disabled = false; });
    this.#listen(this, 'click', event => {
      const button = event.target.closest('[data-circle-open]');
      if (!button || button.closest('story-circles') !== this) return;
      this.#open(this.#items.indexOf(button.closest('[data-circle-item]')));
    });
    this.#listen(document, 'visibilitychange', () => {
      if (document.hidden && this.#dialog?.open) this.#setPaused(true);
    });
    this.#listen(this.#motion, 'change', () => {
      if (this.#motion.matches && this.#dialog?.open) this.#setPaused(true);
    });
    this.#listen(document, FCEvents.mediaPlay, event => {
      if (event.detail?.resource !== this && this.#dialog?.open) this.#setPaused(true);
    });
    this.#listen(document, 'shopify:block:select', event => {
      const item = event.target.closest('[data-circle-item]');
      if (item?.closest('story-circles') !== this) return;
      item.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      const index = this.#items.indexOf(item);
      if (index >= 0 && this.#dialog?.open) { this.#select(index); this.#setPaused(true); }
    });
  }

  disconnectedCallback() {
    this.#stop();
    this.#lifetime?.abort();
    this.#lifetime = null;
    this.#overlay?.closeDialog({ immediate: true, restoreFocus: false });
    this.#overlay?.remove();
    this.#overlay = this.#dialog = null;
  }

  #listen(target, type, callback) {
    target.addEventListener(type, callback, { signal: this.#lifetime.signal });
  }

  #createDialog() {
    this.#overlay = this.querySelector('[data-circles-dialog]').content.firstElementChild.cloneNode(true);
    this.#dialog = this.#overlay.querySelector('dialog');
    this.#dialog.id = `CircleStories-${crypto.randomUUID()}`;
    this.#items.forEach(item => item.querySelector('button').setAttribute('aria-controls', this.#dialog.id));
    this.#items.forEach(() => {
      const segment = document.createElement('span');
      segment.append(document.createElement('i'));
      this.#dialog.querySelector('[data-progress]').append(segment);
    });
    this.#listen(this.#overlay, DialogCloseEvent.eventName, () => {
      this.#stop();
      this.#dialog.querySelector('[data-stage]').replaceChildren();
    });
    this.#listen(this.#overlay, 'click', event => {
      if (event.target.closest('[data-close]')) this.#overlay.closeDialog();
      if (event.target.closest('[data-pause-toggle]')) this.#setPaused(!this.#paused);
      if (event.target.closest('[data-sound]')) {
        this.#muted = !this.#muted;
        const video = this.#dialog.querySelector('video');
        if (video) video.muted = this.#muted;
        this.#syncControls();
      }
      if (event.target.closest('[data-action="previous"]')) this.#select(this.#index - 1);
      if (event.target.closest('[data-action="next"]')) this.#select(this.#index + 1);
    });
    this.#listen(this.#dialog, 'keydown', event => {
      if (event.target.closest('video, a, input, textarea, select')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.#select(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    // Vertical scrolling stays native; horizontal swipes change one story.
    let start;
    this.#listen(this.#dialog, 'touchstart', event => {
      if (event.target.closest('button, a, video')) { start = null; return; }
      start = event.touches.length === 1 ? event.touches[0] : null;
    });
    this.#listen(this.#dialog, 'touchend', event => {
      const end = event.changedTouches[0];
      if (start && Math.abs(end.clientX - start.clientX) > 50 && Math.abs(end.clientY - start.clientY) < 60) {
        this.#select(this.#index + (end.clientX < start.clientX ? 1 : -1));
      }
      start = null;
    });
    document.body.append(this.#overlay);
  }

  #open(index) {
    if (index < 0) return;
    if (!this.#overlay) this.#createDialog();
    // Pointer activation does not focus buttons in every browser. Give the
    // shared dialog an explicit launch target to restore on dismissal.
    this.#items[index].querySelector('[data-circle-open]').focus({ preventScroll: true });
    this.#paused = this.dataset.autoplay !== 'true' || this.#motion.matches;
    this.#muted = true;
    this.#overlay.showDialog();
    this.#select(index);
  }

  #stop() {
    cancelAnimationFrame(this.#frame);
    this.#slideLifetime?.abort();
    this.#dialog?.querySelector('video')?.pause();
  }

  #select(index) {
    if (index < 0 || index >= this.#items.length) return;
    const wasPaused = this.#paused;
    this.#stop();
    this.#paused = wasPaused;
    this.#slideLifetime = new AbortController();
    const { signal } = this.#slideLifetime;
    this.#index = index;
    this.#elapsed = 0;
    this.#lastTime = 0;
    this.#ready = false;
    const template = this.#items[index].querySelector('[data-circle-media]');
    const media = template.content.firstElementChild.cloneNode(true);
    const video = media instanceof HTMLVideoElement;
    this.#dialog.dataset.video = String(video);
    this.#dialog.querySelector('[data-stage]').replaceChildren(media);
    this.#dialog.querySelector('[data-title]').textContent = template.dataset.title;
    this.#dialog.querySelector('[data-position]').textContent = `${index + 1} / ${this.#items.length}`;
    this.#dialog.querySelector('[data-action="previous"]').disabled = index === 0;
    this.#dialog.querySelector('[data-action="next"]').disabled = index === this.#items.length - 1;
    this.#dialog.querySelector('[data-sound]').hidden = !video;
    this.#dialog.querySelector('[data-error-message]').hidden = true;
    const link = this.#dialog.querySelector('[data-link]');
    link.hidden = !template.dataset.link || !template.dataset.linkLabel;
    if (!link.hidden) { link.href = template.dataset.link; link.textContent = template.dataset.linkLabel; }
    const failed = () => {
      this.#setPaused(true);
      const message = this.#dialog.querySelector('[data-error-message]');
      message.textContent = this.dataset.error;
      message.hidden = false;
    };
    media.addEventListener('error', failed, { signal });
    if (video) {
      media.muted = this.#muted;
      media.setAttribute('aria-label', template.dataset.title);
      media.querySelectorAll('img:not([alt])').forEach(image => { image.alt = ''; });
      media.addEventListener('playing', () => {
        this.#ready = true; this.#paused = false; this.#syncControls();
        this.dispatchEvent(new MediaPlayEvent(this));
      }, { signal });
      media.addEventListener('pause', () => { this.#paused = true; this.#syncControls(); }, { signal });
      media.addEventListener('volumechange', () => { this.#muted = media.muted; this.#syncControls(); }, { signal });
      media.addEventListener('ended', () => {
        if (this.#autoAdvance) this.#advance();
      }, { signal });
      media.load();
    } else {
      media.addEventListener('load', () => { this.#ready = true; }, { signal });
      this.#ready = media.complete && media.naturalWidth > 0;
    }
    this.#setPaused(wasPaused);
    const focused = document.activeElement;
    if (this.#dialog.contains(focused) && (focused.matches('button:disabled') || focused.hidden)) {
      this.#dialog.querySelector('[data-pause-toggle]').focus({ preventScroll: true });
    }
    this.#paintProgress(0);
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #advance() {
    if (this.#index < this.#items.length - 1) {
      this.#paused = false;
      this.#select(this.#index + 1);
    } else this.#setPaused(true);
  }

  #setPaused(paused) {
    this.#paused = paused;
    const video = this.#dialog?.querySelector('video');
    if (video) {
      if (paused) video.pause();
      else {
        if (video.ended) video.currentTime = 0;
        video.play().catch(() => {
          if (video !== this.#dialog?.querySelector('video')) return;
          this.#paused = true;
          this.#syncControls();
        });
      }
    } else if (!paused && this.#elapsed >= this.#duration) this.#elapsed = 0;
    this.#syncControls();
  }

  get #autoAdvance() { return this.dataset.autoplay === 'true' && !this.#motion.matches; }

  get #duration() { return (Number(this.dataset.duration) || 5) * 1000; }

  #syncControls() {
    if (!this.#dialog) return;
    const button = this.#dialog.querySelector('[data-pause-toggle]');
    button.setAttribute('aria-label', this.#paused ? this.dataset.play : this.dataset.pause);
    button.querySelector('[data-play-icon]').hidden = !this.#paused;
    button.querySelector('[data-pause-icon]').hidden = this.#paused;
    this.#dialog.querySelector('[data-sound]').textContent = this.#muted ? this.dataset.unmute : this.dataset.mute;
  }

  #paintProgress(progress) {
    this.#dialog.querySelectorAll('[data-progress] i').forEach((bar, index) => {
      bar.style.setProperty('--progress', index < this.#index ? 1 : index === this.#index ? Math.min(1, progress) : 0);
    });
  }

  #tick = time => {
    if (!this.#dialog?.open) return;
    const delta = this.#lastTime ? Math.min(100, time - this.#lastTime) : 0;
    this.#lastTime = time;
    const video = this.#dialog.querySelector('video');
    if (video) this.#paintProgress(Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration : 0);
    else if (!this.#paused && this.#ready && !document.hidden) {
      this.#elapsed += delta;
      this.#paintProgress(this.#elapsed / this.#duration);
      if (this.#elapsed >= this.#duration) {
        if (this.#autoAdvance) this.#advance();
        else this.#setPaused(true);
        if (!this.#paused) return; // The next slide owns its animation frame.
      }
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('story-circles')) customElements.define('story-circles', StoryCircles);
